import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
  MapPinIcon,
  GlobeAltIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { TrophyIcon } from '@heroicons/react/24/solid';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import CourseModal, { type Course } from './courses/CourseModal';
import HoleEditor from './courses/HoleEditor';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

type View = 'list' | 'holes';

export default function Courses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [view, setView] = useState<View>('list');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [deletingCourse, setDeletingCourse] = useState<Course | null>(null);
  const [holeCountsMap, setHoleCountsMap] = useState<Record<string, number>>({});

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .order('name');
      if (error) throw error;
      setCourses((data as Course[]) ?? []);

      if (data && data.length > 0) {
        const ids = data.map((c) => c.id);
        const { data: holeCounts } = await supabase
          .from('holes')
          .select('course_id')
          .in('course_id', ids);
        if (holeCounts) {
          const counts: Record<string, number> = {};
          for (const row of holeCounts) {
            counts[row.course_id] = (counts[row.course_id] ?? 0) + 1;
          }
          setHoleCountsMap(counts);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleDelete = async () => {
    if (!deletingCourse) return;
    try {
      const { error } = await supabase.from('courses').delete().eq('id', deletingCourse.id);
      if (error) throw error;
      setCourses((prev) => prev.filter((c) => c.id !== deletingCourse.id));
      toast.success('Course deleted');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete course');
    } finally {
      setDeletingCourse(null);
    }
  };

  const filtered = courses.filter((c) => {
    const matchSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.city ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (c.country ?? '').toLowerCase().includes(search.toLowerCase());
    const matchActive =
      filterActive === 'all' ||
      (filterActive === 'active' && c.is_active) ||
      (filterActive === 'inactive' && !c.is_active);
    return matchSearch && matchActive;
  });

  const activeCourses = courses.filter((c) => c.is_active).length;

  if (view === 'holes' && selectedCourse) {
    return (
      <HoleEditor
        course={selectedCourse}
        onBack={() => {
          setView('list');
          setSelectedCourse(null);
          fetchCourses();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-golf-gray">Courses</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage golf courses and hole layouts</p>
        </div>
        <button
          onClick={() => { setEditingCourse(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-golf-orange text-white font-semibold text-sm rounded-xl hover:bg-golf-orange/90 transition-colors shadow-sm shadow-golf-orange/20"
        >
          <PlusIcon className="w-4 h-4" />
          Add Course
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-golf-green/10 flex items-center justify-center flex-shrink-0">
            <TrophyIcon className="w-5 h-5 text-golf-green" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Courses</p>
            <p className="text-2xl font-bold text-golf-gray">{courses.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
            <span className="text-golf-orange font-bold text-xs">ACT</span>
          </div>
          <div>
            <p className="text-xs text-gray-500">Active</p>
            <p className="text-2xl font-bold text-golf-gray">{activeCourses}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <AdjustmentsHorizontalIcon className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Holes Defined</p>
            <p className="text-2xl font-bold text-golf-gray">
              {Object.values(holeCountsMap).reduce((a, b) => a + b, 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search courses..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
            />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {(['all', 'active', 'inactive'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilterActive(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all ${
                  filterActive === f ? 'bg-white text-golf-gray shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-orange border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <TrophyIcon className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-gray-500 font-medium">No courses found</p>
            <p className="text-sm text-gray-400 mt-1">
              {search ? 'Try a different search term' : 'Add your first golf course'}
            </p>
            {!search && (
              <button
                onClick={() => { setEditingCourse(null); setShowModal(true); }}
                className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-golf-orange border border-golf-orange/30 rounded-xl hover:bg-golf-orange/5 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add First Course
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            <AnimatePresence>
              {filtered.map((course) => {
                const definedHoles = holeCountsMap[course.id] ?? 0;
                const completion = Math.round((definedHoles / course.holes_count) * 100);

                return (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center">
                      {course.logo_url ? (
                        <img src={course.logo_url} alt={course.name} className="w-full h-full object-contain p-1" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : course.image_url ? (
                        <img src={course.image_url} alt={course.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <TrophyIcon className="w-6 h-6 text-gray-300" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-golf-gray truncate">{course.name}</h3>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          course.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {course.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-golf-sand/50 text-golf-gray">
                          {course.holes_count} holes · Par {course.par}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {(course.city || course.country) && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPinIcon className="w-3 h-3" />
                            {[course.city, course.country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {course.course_rating && (
                          <span className="text-xs text-gray-400">Rating: {course.course_rating}</span>
                        )}
                        {course.slope_rating && (
                          <span className="text-xs text-gray-400">Slope: {course.slope_rating}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 max-w-[120px] bg-gray-100 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-500 ${
                              completion === 100 ? 'bg-golf-green' : completion > 0 ? 'bg-golf-orange' : 'bg-gray-300'
                            }`}
                            style={{ width: `${completion}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">{definedHoles}/{course.holes_count} holes defined</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => { setSelectedCourse(course); setView('holes'); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-golf-green border border-golf-green/30 bg-golf-green/5 hover:bg-golf-green/10 rounded-lg transition-colors"
                        title="Edit holes"
                      >
                        <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                        Holes
                      </button>
                      <button
                        onClick={() => { setEditingCourse(course); setShowModal(true); }}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-golf-gray"
                        title="Edit course"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeletingCourse(course)}
                        className="p-2 rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
                        title="Delete course"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
            {filtered.length} course{filtered.length !== 1 ? 's' : ''} shown
          </div>
        )}
      </div>

      {showModal && (
        <CourseModal
          course={editingCourse}
          onClose={() => { setShowModal(false); setEditingCourse(null); }}
          onSaved={(saved) => {
            if (editingCourse) {
              setCourses((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
            } else {
              setCourses((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
            }
            setShowModal(false);
            setEditingCourse(null);
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingCourse}
        title="Delete Course"
        message={deletingCourse ? `Are you sure you want to delete "${deletingCourse.name}"? This will also delete all hole data for this course.` : ''}
        onConfirm={handleDelete}
        onClose={() => setDeletingCourse(null)}
      />
    </div>
  );
}
